/**
 * Visualization - Handles D3 bar chart rendering
 */

import * as d3 from 'd3';
import { AthleteData } from '../types/athlete.types';
import { RankingMode, VisualizationConfig } from '../types/config.types';
import { formatTime, formatTimeForAxis } from '../utils/timeFormatter';
import { DEFAULT_MARGIN, BAR_CONFIG, MAX_VISIBLE_HEIGHT, ANIMATION_DURATION } from '../constants';

export class Visualization {
    private config: VisualizationConfig;
    private svg: any = null;
    private g: any = null;
    private xAxisSvg: any = null;
    private xScale: d3.ScaleLinear<number, number> | null = null;
    private currentStage: number = 1;
    private onAthleteClick: (athlete: AthleteData) => void;
    private onAthleteHover: (event: MouseEvent, athlete: AthleteData) => void;
    private onAthleteHoverEnd: () => void;

    constructor(
        onAthleteClick: (athlete: AthleteData) => void,
        onAthleteHover: (event: MouseEvent, athlete: AthleteData) => void,
        onAthleteHoverEnd: () => void
    ) {
        this.config = {
            margin: DEFAULT_MARGIN,
            barHeight: BAR_CONFIG.maxHeight,
            barPadding: BAR_CONFIG.padding,
            maxBarHeight: BAR_CONFIG.maxHeight,
            minBarHeight: BAR_CONFIG.minHeight,
            maxVisibleHeight: MAX_VISIBLE_HEIGHT
        };

        this.onAthleteClick = onAthleteClick;
        this.onAthleteHover = onAthleteHover;
        this.onAthleteHoverEnd = onAthleteHoverEnd;
    }

    /**
     * Initializes the SVG structure
     */
    private initialize(): void {
        d3.select('#chart-container').selectAll('*').remove();
        d3.select('#x-axis-container').selectAll('*').remove();

        const containerWidth = document.getElementById('chart-container')!.offsetWidth;
        const width = containerWidth - this.config.margin.left - this.config.margin.right;

        this.svg = d3.select<HTMLDivElement, unknown>('#chart-container')
            .append('svg')
            .attr('width', containerWidth);

        this.g = this.svg.append('g')
            .attr('transform', `translate(${this.config.margin.left},${this.config.margin.top})`);

        this.xAxisSvg = d3.select<HTMLDivElement, unknown>('#x-axis-container')
            .append('svg')
            .attr('width', containerWidth)
            .attr('height', 40);

        this.xScale = d3.scaleLinear().range([0, width]);
    }

    /**
     * Calculates dynamic bar height based on number of participants
     */
    private calculateBarHeight(numParticipants: number): number {
        const availableHeight = this.config.maxVisibleHeight - this.config.margin.top - this.config.margin.bottom;
        const totalSpaceNeeded = numParticipants * (this.config.maxBarHeight + this.config.barPadding);

        if (totalSpaceNeeded <= availableHeight) {
            return this.config.maxBarHeight;
        }

        const dynamicHeight = Math.floor((availableHeight / numParticipants) - this.config.barPadding);
        return Math.max(dynamicHeight, this.config.minBarHeight);
    }

    /**
     * Updates the visualization with new data
     */
    update(
        filteredData: AthleteData[],
        rankingMode: RankingMode,
        currentStage: number,
        selectedAthleteBib: number | null = null
    ): void {
        // Initialize SVG structure if not already done
        if (!this.svg || !this.g || !this.xScale || !this.xAxisSvg) {
            this.initialize();
        }

        this.currentStage = currentStage;

        if (filteredData.length === 0) {
            this.showNoDataMessage();
            return;
        }

        this.svg.selectAll('foreignObject').remove();

        // Calculate dynamic bar height
        this.config.barHeight = this.calculateBarHeight(filteredData.length);

        // Update SVG height
        const height = filteredData.length * (this.config.barHeight + this.config.barPadding) +
                      this.config.margin.top + this.config.margin.bottom;
        this.svg.attr('height', height);

        // Update x-scale domain
        const maxTime = rankingMode === 'individual'
            ? d3.max(filteredData, d => d.individualRaceTime || 0)!
            : d3.max(filteredData, d => d.stageTime)!;
        this.xScale.domain([0, maxTime]);

        this.renderBars(filteredData, rankingMode, selectedAthleteBib);
        this.renderXAxis(rankingMode);
        this.renderYAxisLabel(height);
    }

    /**
     * Renders the athlete bars
     */
    private renderBars(
        filteredData: AthleteData[],
        rankingMode: RankingMode,
        selectedAthleteBib: number | null
    ): void {
        if (!this.g || !this.xScale) return;

        const bars = this.g.selectAll('.athlete-bar')
            .data(filteredData, (d: any) => d.Bib);

        // EXIT
        bars.exit()
            .transition()
            .duration(500)
            .style('opacity', 0)
            .remove();

        // ENTER
        const barGroupsEnter = bars.enter()
            .append('g')
            .attr('class', 'athlete-bar')
            .style('opacity', 0);

        barGroupsEnter.append('rect')
            .attr('height', this.config.barHeight)
            .attr('width', 0);

        barGroupsEnter.append('text')
            .attr('class', 'rank-label')
            .attr('x', -10)
            .attr('y', this.config.barHeight / 2)
            .attr('text-anchor', 'end')
            .attr('dominant-baseline', 'middle');

        barGroupsEnter.append('text')
            .attr('class', 'name-label')
            .attr('x', 5)
            .attr('y', this.config.barHeight / 2)
            .attr('dominant-baseline', 'middle');

        barGroupsEnter.append('text')
            .attr('class', 'time-label')
            .attr('y', this.config.barHeight / 2)
            .attr('dominant-baseline', 'middle')
            .style('fill', '#666');

        barGroupsEnter
            .on('mouseover', (event, d) => this.onAthleteHover(event, d))
            .on('mouseout', () => this.onAthleteHoverEnd())
            .on('click', (_, d) => this.onAthleteClick(d));

        // UPDATE
        const barGroupsUpdate = barGroupsEnter.merge(bars as any);

        barGroupsUpdate
            .transition()
            .duration(ANIMATION_DURATION)
            .ease(d3.easeQuadOut)
            .style('opacity', 1)
            .attr('transform', (_: any, i: number) =>
                `translate(0, ${i * (this.config.barHeight + this.config.barPadding)})`
            );

        this.updateRectangles(barGroupsUpdate, rankingMode);
        this.updateRankLabels(barGroupsUpdate, filteredData.length);
        this.updateNameLabels(barGroupsUpdate);
        this.updateTimeLabels(barGroupsUpdate, rankingMode);

        // Restore selection
        if (selectedAthleteBib !== null) {
            barGroupsUpdate.classed('selected', (d: any) => d.Bib === selectedAthleteBib);
        }
    }

    private updateRectangles(
        selection: d3.Selection<d3.BaseType, AthleteData, SVGGElement, unknown>,
        rankingMode: RankingMode
    ): void {
        if (!this.xScale) return;

        selection.select('rect')
            .transition()
            .duration(ANIMATION_DURATION)
            .ease(d3.easeQuadOut)
            .attr('height', this.config.barHeight)
            .attr('width', d => {
                const timeValue = rankingMode === 'individual'
                    ? (d.individualRaceTime || 0)
                    : d.stageTime;
                return this.xScale!(timeValue);
            });
    }

    private updateRankLabels(
        selection: d3.Selection<d3.BaseType, AthleteData, SVGGElement, unknown>,
        dataLength: number
    ): void {
        selection.select('.rank-label')
            .attr('y', this.config.barHeight / 2)
            .style('font-size', (_: any, i: number) => {
                const isFirstOrLast = (i === 0 || i === dataLength - 1);
                return isFirstOrLast ? '12px' : Math.min(12, this.config.barHeight * 0.6) + 'px';
            })
            .style('display', (_: any, i: number) => {
                const isFirstOrLast = (i === 0 || i === dataLength - 1);
                return (this.config.barHeight >= 20 || isFirstOrLast) ? 'block' : 'none';
            })
            .transition()
            .duration(500)
            .tween('text', function(this: SVGTextElement, d: any) {
                const node = this as SVGTextElement;
                const i = d3.interpolateNumber(+(node.textContent || '0'), d.filteredRank);
                return function(t: number) {
                    node.textContent = Math.round(i(t)).toString();
                };
            });
    }

    private updateNameLabels(
        selection: d3.Selection<d3.BaseType, AthleteData, SVGGElement, unknown>
    ): void {
        selection.select('.name-label')
            .attr('y', this.config.barHeight / 2)
            .style('font-size', Math.min(12, this.config.barHeight * 0.6) + 'px')
            .style('display', this.config.barHeight < 20 ? 'none' : 'block')
            .text(d => d.Name);
    }

    private updateTimeLabels(
        selection: d3.Selection<d3.BaseType, AthleteData, SVGGElement, unknown>,
        rankingMode: RankingMode
    ): void {
        if (!this.xScale) return;

        selection.select('.time-label')
            .attr('y', this.config.barHeight / 2)
            .style('font-size', Math.min(11, this.config.barHeight * 0.55) + 'px')
            .style('display', this.config.barHeight < 20 ? 'none' : 'block')
            .text(d => {
                const timeValue = rankingMode === 'individual'
                    ? (d.individualRaceTime || 0)
                    : d.stageTime;
                return formatTime(timeValue);
            })
            .transition()
            .duration(ANIMATION_DURATION)
            .attr('x', d => {
                const timeValue = rankingMode === 'individual'
                    ? (d.individualRaceTime || 0)
                    : d.stageTime;
                return this.xScale!(timeValue) + 5;
            });
    }

    private renderXAxis(rankingMode: RankingMode): void {
        if (!this.xScale || !this.xAxisSvg) return;

        const xAxis = d3.axisBottom(this.xScale)
            .tickFormat(d => formatTimeForAxis(d as number));

        this.xAxisSvg.selectAll('*').remove();
        this.xAxisSvg.append('g')
            .attr('transform', `translate(${this.config.margin.left}, 0)`)
            .call(xAxis);

        const axisLabel = rankingMode === 'individual'
            ? `Race ${this.currentStage} Time`
            : 'Cumulative Time';

        this.xAxisSvg.append('text')
            .attr('x', (+this.xAxisSvg.attr('width') / 2))
            .attr('y', 35)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('fill', 'white')
            .text(axisLabel);
    }

    private renderYAxisLabel(height: number): void {
        if (!this.svg) return;

        this.svg.selectAll('.y-axis-label').remove();
        this.svg.append('text')
            .attr('class', 'y-axis-label')
            .attr('x', -height / 2)
            .attr('y', 15)
            .attr('text-anchor', 'middle')
            .attr('transform', 'rotate(-90)')
            .style('font-size', '12px')
            .style('fill', 'white')
            .text('Rank');
    }

    private showNoDataMessage(): void {
        if (!this.svg || !this.g) return;

        this.g.selectAll('*').remove();
        this.svg.append('foreignObject')
            .attr('width', '100%')
            .attr('height', 100)
            .append('xhtml:div')
            .style('text-align', 'center')
            .style('padding-top', '50px')
            .html('<div class="loading">No athletes match the selected filters</div>');
    }

    /**
     * Shows loading state
     */
    showLoading(): void {
        d3.select('#chart-container')
            .html('<div class="loading">Loading race data...</div>');
    }

    /**
     * Shows error message
     */
    showError(message: string): void {
        d3.select('#chart-container')
            .html(`<div class="loading">${message}</div>`);
    }

    /**
     * Gets the main group for adding overlays
     */
    getGroup(): d3.Selection<SVGGElement, unknown, null, undefined> | null {
        return this.g;
    }

    /**
     * Gets the x-scale
     */
    getXScale(): d3.ScaleLinear<number, number> | null {
        return this.xScale;
    }

    /**
     * Gets current bar configuration
     */
    getBarConfig(): { height: number; padding: number } {
        return {
            height: this.config.barHeight,
            padding: this.config.barPadding
        };
    }
}
